import { useState, useCallback } from 'react';
import { snowflakeService, DataForSeoResultData } from '../services/snowflakeService';
import { useToast } from '../context/ToastContext';

interface DataForSeoBatchResult {
  batch_id: string;
  total_keywords: number;
  processing_time_seconds: number;
  success_count: number;
  failed_count: number;
  blob_container: string;
  blob_prefix: string;
  download_urls: {
    results_file: string;
    summary_report: string;
  };
  summary_metrics: {
    total_search_volume: number;
    average_search_volume: number;
    zero_volume_keywords: number;
    high_volume_keywords: number;
    average_competition: number;
    average_cpc: number;
  };
}

export const useDataForSeoSnowflakeIntegration = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { showToast } = useToast();

  const saveDataForSeoResultToSnowflake = useCallback(async (
    result: DataForSeoBatchResult,
    keywords: string[],
    locationCode: number,
    languageCode: string,
    workflowId?: string,
    workflowName?: string,
    clientId?: string,
    clientName?: string,
    detailedResults?: any[]
  ) => {
    console.log('🚀 saveDataForSeoResultToSnowflake called with:', {
      batch_id: result.batch_id,
      keywords_count: keywords.length,
      locationCode,
      languageCode,
      workflowId,
      clientId,
      using_simplified_pattern: true
    });
    
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

      try {
        console.log('Starting DataForSEO Snowflake save process (simplified pattern)...');

        // We no longer fetch or include detailed results - just store the blob path
        // This follows the same pattern as SERP results

      // Prepare the data for Snowflake simplified schema (no detailed results)
      const dataForSeoData: DataForSeoResultData = {
        batch_id: result.batch_id,
        location_code: locationCode,
        language_code: languageCode,
        keywords: keywords,
        keyword_results: [], // Empty array - we store blob path instead
        processing_metadata: {
          total_keywords: result.total_keywords,
          processing_time_seconds: result.processing_time_seconds,
          success_count: result.success_count,
          failed_count: result.failed_count,
          source: 'dataforseo-tool',
          created_at: new Date().toISOString(),
          summary_metrics: result.summary_metrics // Include summary metrics
        },
        blob_urls: {
          blob_container: result.blob_container,
          blob_prefix: result.blob_prefix,
          results_file: result.download_urls.results_file,
          summary_report: result.download_urls.summary_report
        },
        client_id: clientId,
        client_name: clientName,
        workflow_context: workflowId ? {
          workflow_id: workflowId,
          workflow_name: workflowName
        } : undefined
      };

      console.log('Prepared DataForSEO data for Snowflake simplified schema:', {
        batch_id: dataForSeoData.batch_id,
        location_code: dataForSeoData.location_code,
        language_code: dataForSeoData.language_code,
        keyword_count: dataForSeoData.keywords.length,
        blob_urls: dataForSeoData.blob_urls,
        processing_metadata: dataForSeoData.processing_metadata
      });

      // Save to Snowflake
      const snowflakeResult = await snowflakeService.saveDataForSeoResult(dataForSeoData);

      if (snowflakeResult.success) {
        console.log('✅ DataForSEO analysis saved to Snowflake successfully:', snowflakeResult.data);
        setSaveSuccess(true);
        showToast('DataForSEO analysis saved to Snowflake successfully', 'success');
        
        // Log the saved data for debugging
        console.log('Snowflake save response:', {
          success: true,
          data: snowflakeResult.data,
          batch_id: result.batch_id
        });
      } else {
        console.error('❌ Failed to save DataForSEO analysis to Snowflake:', snowflakeResult.error);
        console.error('Full error response:', snowflakeResult);
        setSaveError(snowflakeResult.error || 'Unknown error occurred');
        showToast(`Failed to save DataForSEO analysis to Snowflake: ${snowflakeResult.error}`, 'error');
      }

    } catch (error: any) {
      console.error('❌ Unexpected error saving DataForSEO analysis to Snowflake:', error);
      setSaveError(error.message || 'Unexpected error occurred');
      showToast(`Error saving DataForSEO analysis to Snowflake: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  }, [showToast]);

  return {
    saveDataForSeoResultToSnowflake,
    isSaving,
    saveError,
    saveSuccess,
    resetState: useCallback(() => {
      setSaveError(null);
      setSaveSuccess(false);
      setIsSaving(false);
    }, [])
  };
}; 